import { useUserContext, useUTLSettingsContext } from "@dexteel/mesf-core";
import { useLocation, useNavigate } from "react-router-dom";
import { useMapContext } from "../context/MapContext";
import { useSetersToMap } from "./useSetersToMap";
import type { AssetFromMap } from "../models/AssetFromMap";
import { get } from "lodash-es";

const CLIENT = import.meta.env.VITE_CLIENT || "MELT";

export const useCreateMap = () => {
	const {
		state: { currAssetId },
		actions: {
			setLoadingMap,
			setClicked,
			setAnchorPointY,
			setAnchorPointX,
			setShowContextMenu,
			setMenuContextAssetId,
			setCurrAssetId,
			setListHiddenChildrenIds,
			setShowHiddenChildrenButton,
		},
	} = useMapContext();
	const {
		state: { settings },
	} = useUTLSettingsContext();

	const hideBlueBoxes = !!get(settings, "HideBlueBoxes", true);

	const list: number[] = [];
	const navigate = useNavigate();
	const location = useLocation();

	const { setArrowColorAndInfo, getForeignObject } = useSetersToMap();

	const setRectsInfo = (
		assetId: number,
		title: string,
		text1: string,
		text2: string,
		text3: string,
		ChildrenCount: number,
		isActive: boolean,
	) => {
		const id = "assetId-" + assetId;
		const assetGroup = document.getElementById(id);
		if (assetGroup && !isActive) {
			assetGroup!.style.display = "none";
			return;
		}
		if (assetGroup) {
			let text = "";
			let foreignObject = null;
			for (let i = 0; i < assetGroup.children.length; i++) {
				if (
					assetGroup.children[i].classList[0] === "r3" ||
					assetGroup.children[i].classList[0] === "r4" ||
					assetGroup.children[i].classList[0] === "r5"
				) {
					if (hideBlueBoxes) {
						if (assetGroup.children[i].classList[0] === "r3") {
							assetGroup.children[i].setAttribute("display", "none");
							text = "";
						}
					} else {
						if (assetGroup.children[i].classList[0] === "r3") {
							assetGroup.children[i].setAttribute("fill", "rgb(0, 80, 239)");
							text = text1;
						}
					}
					if (assetGroup.children[i].classList[0] === "r4") {
						assetGroup.children[i].setAttribute("fill", "rgb(0, 138, 0)");
						text = text2;
					}
					if (assetGroup.children[i].classList[0] === "r5") {
						assetGroup.children[i].setAttribute("fill", "rgb(240, 163, 10)");
						text = text3;
					}
					if (
						(CLIENT === "MELT" && currAssetId === 1) ||
						(CLIENT === "CRANES" && currAssetId === 10000)
					) {
						if (
							assetId === 2 ||
							assetId === 746 ||
							assetId === 890 ||
							assetId === 891 ||
							CLIENT === "CRANES"
						)
							foreignObject = getForeignObject(
								assetGroup.children[i],
								text,
								"white",
								"8px",
								"center",
							);
						else
							foreignObject = getForeignObject(
								assetGroup.children[i],
								text,
								"white",
								"11px",
								"center",
							);
					} else {
						foreignObject = getForeignObject(
							assetGroup.children[i],
							text,
							"white",
							null,
							"center",
						);
					}
					assetGroup.appendChild(foreignObject);
				} else if (assetGroup.children[i].classList[0] === "r2") {
					if (
						(CLIENT === "MELT" && currAssetId === 1) ||
						(CLIENT === "CRANES" && currAssetId === 10000)
					) {
						if (
							assetId === 2 ||
							assetId === 746 ||
							assetId === 890 ||
							assetId === 891 ||
							CLIENT === "CRANES"
						)
							foreignObject = getForeignObject(
								assetGroup.children[i],
								title,
								"black",
								"9px",
								"center",
							);
						else
							foreignObject = getForeignObject(
								assetGroup.children[i],
								title,
								"black",
								"12px",
								"center",
							);
					} else {
						foreignObject = getForeignObject(
							assetGroup.children[i],
							title,
							"black",
							null,
							"center",
						);
					}

					if (ChildrenCount === 0)
						assetGroup.children[i].setAttribute("fill", "lightgray");
					else assetGroup.children[i].setAttribute("fill", "lightblue");
					assetGroup.appendChild(foreignObject);
				}
				if (assetGroup.children[i].classList[0] === "r1") {
					if (ChildrenCount === 0)
						assetGroup.children[i].setAttribute("fill", "lightgray");
					else assetGroup.children[i].setAttribute("fill", "lightblue");
				}
				if (assetGroup.children[i].classList[1] === "r1") {
					if (ChildrenCount === 0)
						assetGroup.children[i].setAttribute("fill", "lightgray");
					else assetGroup.children[i].setAttribute("fill", "lightblue");
				}
				if (assetGroup.children[i].classList[0] === "l1") {
					if (ChildrenCount === 0) {
						assetGroup.children[i].setAttribute("stroke", "lightgray");
						assetGroup.children[i].setAttribute("fill", "lightgray");
					} else {
						assetGroup.children[i].setAttribute("stroke", "lightblue");
						assetGroup.children[i].setAttribute("fill", "lightblue");
					}
				}
			}
		} else if (assetGroup == null) {
			setListOfLosing(assetId);
			setShowHiddenChildrenButton(true);
		}
	};

	const setListOfLosing = async (children: number) => {
		await list.push(children);
		setListHiddenChildrenIds(list);
	};

	const URLManagement = (assetNameString: string) => {
		const newURL = "/map/" + assetNameString + "-" + currAssetId;
		const currURL = location.pathname.toString();

		if (currURL !== newURL) {
			navigate(newURL);
		}
	};

	const addEvents = (assetId: number, title: string, ChildrenCount: number) => {
		const id = "assetId-" + assetId;
		const assetGroup = document.getElementById(id);
		if (assetGroup) {
			if (ChildrenCount > 0) {
				assetGroup.addEventListener(
					"click",
					(e) => {
						e.preventDefault();
						setClicked(true);
						setLoadingMap(true);
						setCurrAssetId(assetId);
					},
					false,
				);
			}

			if (!ChildrenCount) {
				assetGroup.addEventListener(
					"click",
					(e) => {
						e.preventDefault();
						setClicked(true);
						setLoadingMap(true);
						navigate(
							`/asset-details/${assetId}?resource=documents&assets=true&statistics=true&workOrders=true`,
						);
					},
					false,
				);
			}
			if (assetId) {
				assetGroup.addEventListener("contextmenu", (e: MouseEvent) => {
					e.preventDefault();
					e.stopPropagation();
					setAnchorPointX(e.pageX);
					setAnchorPointY(e.pageY);
					setShowContextMenu(true);
					setMenuContextAssetId(assetId);
				});
			}
		}
	};

	const setValuesAndEvents = (
		assetId: number,
		title: string,
		text1: string,
		text2: string,
		text3: string,
		ChildrenCount: number,
		isActive: boolean,
	) => {
		setRectsInfo(assetId, title, text1, text2, text3, ChildrenCount, isActive);
		addEvents(assetId, title, ChildrenCount);
	};

	const loadAssetComponent = (
		title: string,
		assetsProps: AssetFromMap,
		assetNameString: string,
		svgMap: string,
		rmWorkOrders: string,
		pmWorkOrders: string,
		pmWorkOrdersClosed: string,
		rmWorkOrdersClosed: string,
		isActive: boolean,
	) => {
		setLoadingMap(false);
		const main = document.getElementById("main");
		if (main) {
			main.innerHTML = svgMap;

			// Remove legacy header rectangle (from old parent title/stats)
			// Direct removal by class name "title" and any white rects at x=0, y=0
			const titleRects = main.querySelectorAll("rect.title");
			titleRects.forEach((rect) => rect.remove());

			// Also remove any white rectangles at origin (x=0, y=0) that are reasonably large
			const allRects = main.querySelectorAll("rect");
			allRects.forEach((rect) => {
				const x = Number.parseFloat(rect.getAttribute("x") || "0");
				const y = Number.parseFloat(rect.getAttribute("y") || "0");
				const width = Number.parseFloat(rect.getAttribute("width") || "0");
				const height = Number.parseFloat(rect.getAttribute("height") || "0");
				const fill = rect.getAttribute("fill") || "";
				const fillLower = fill.toLowerCase().trim();

				// Remove white rectangles at origin (0,0) that are header-sized
				if (
					x === 0 &&
					y === 0 &&
					width > 200 &&
					height > 50 &&
					(fillLower.includes("255, 255, 255") ||
						fillLower === "white" ||
						fillLower === "#fff" ||
						fillLower === "#ffffff" ||
						fillLower === "rgb(255, 255, 255)" ||
						fillLower === "rgb(255,255,255)")
				) {
					rect.remove();
				}
			});

			setArrowColorAndInfo(document.getElementById("main"));

			let text1, text2, text3;
			assetsProps.forEach((elem: AssetFromMap) => {
				if (!elem.IsActive) {
					return;
				}
				text1 = `Closed: ${
					((elem.PMWorkOrdersChildrenClosed as string) +
						elem.RMWorkOrdersChildrenClosed) as string
				}`;
				text2 = elem.PMWorkOrdersChildren;
				text3 = elem.RMWorkOrdersChildren;
				setValuesAndEvents(
					elem.AssetId,
					elem.AssetName,
					text1,
					text2 as string,
					text3 as string,
					elem.ChildrenCount as number,
					elem.IsActive,
				);
			});

			URLManagement(assetNameString);
		}
	};

	const createGenericMap = (
		assetName: string,
		assetsProps: AssetFromMap,
		assetNameString: string,
		rmWorkOrders: string,
		pmWorkOrders: string,
		pmWorkOrdersClosed: string,
		rmWorkOrdersClosed: string,
		isActive: boolean,
	) => {
		let buf = "<g>";

		let px = 20;
		let py = 20;
		assetsProps.forEach((item: AssetFromMap) => {
			if (!item.IsActive) {
				return;
			}
			buf = buf + '<g id="assetId-' + item.AssetId + '" >';

			buf =
				buf +
				'<rect  x="' +
				px +
				'" y=" ' +
				py +
				'" width="250" height="160" fill="rgb(255, 255, 255)" stroke="rgb(0, 0, 0)" style="cursor:pointer" class="r1"   />';
			buf =
				buf +
				'<rect  x="' +
				(px + 38) +
				'" y=" ' +
				(py + 38.09 - 20.0) +
				'" width="170" height="70" fill="rgb(255, 255, 255)" stroke="none"         style="cursor:pointer" class="r2"    />';
			if (!hideBlueBoxes) {
				buf =
					buf +
					'<rect  x="' +
					(px + 40.31) +
					'" y=" ' +
					(py + 189.47 - 70.0) +
					'" width="169.38" height="23.13" fill="rgb(255, 255, 255)" stroke="rgb(0, 0, 0)" style="cursor:pointer" class="r3"     />';
			}
			buf =
				buf +
				'<rect  x="' +
				(px + 40.31) +
				'" y=" ' +
				(py + 161.35 - 70.0) +
				'" width="80" height="23.13" fill="rgb(255, 255, 255)" stroke="rgb(0, 0, 0)" style="cursor:pointer" class="r4"      />';
			buf =
				buf +
				'<rect  x="' +
				(px + 130.31) +
				'" y=" ' +
				(py + 161.35 - 70.0) +
				'" width="80" height="23.13" fill="rgb(255, 255, 255)" stroke="rgb(0, 0, 0)" style="cursor:pointer" class="r5"      />';
			buf = buf + "</g>";

			px = px + 260;
			if (px > 1800) {
				py = py + 180;
				px = 20;
			}
		});
		buf = buf + "</g>";
		loadAssetComponent(
			assetName,
			assetsProps,
			assetNameString,
			buf,
			rmWorkOrders,
			pmWorkOrders,
			pmWorkOrdersClosed,
			rmWorkOrdersClosed,
			isActive,
		);
	};

	return { loadAssetComponent, createGenericMap };
};
